import { Orbit, Sparkles } from "lucide-react";

import { pickText } from "@/lib/content";

const RitualBodySVG = () => (
  <svg
    className="absolute inset-0 h-full w-full"
    fill="none"
    preserveAspectRatio="xMidYMid slice"
    viewBox="0 0 400 500"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Background sacred geometry */}
    <defs>
      <radialGradient id="crownGlow" cx="50%" cy="10%" r="30%">
        <stop offset="0%" stopColor="rgba(212, 175, 55, 0.3)" />
        <stop offset="100%" stopColor="transparent" />
      </radialGradient>
      <radialGradient id="heartGlow" cx="50%" cy="35%" r="20%">
        <stop offset="0%" stopColor="rgba(212, 175, 55, 0.15)" />
        <stop offset="100%" stopColor="transparent" />
      </radialGradient>
      <linearGradient id="centralPillar" x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor="rgba(212, 175, 55, 0.6)" />
        <stop offset="50%" stopColor="rgba(255, 255, 255, 0.3)" />
        <stop offset="100%" stopColor="rgba(212, 175, 55, 0.4)" />
      </linearGradient>
      <linearGradient id="pillarLeft" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="rgba(62, 76, 89, 0.4)" />
        <stop offset="100%" stopColor="rgba(62, 76, 89, 0.1)" />
      </linearGradient>
      <linearGradient id="pillarRight" x1="100%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="rgba(212, 175, 55, 0.25)" />
        <stop offset="100%" stopColor="rgba(212, 175, 55, 0.05)" />
      </linearGradient>
      <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Sacred geometry background circles */}
    <circle cx="200" cy="250" r="180" stroke="rgba(212, 175, 55, 0.1)" strokeWidth="0.5" fill="none" />
    <circle cx="200" cy="250" r="140" stroke="rgba(255, 255, 255, 0.05)" strokeWidth="0.5" fill="none" />
    <circle cx="200" cy="250" r="100" stroke="rgba(212, 175, 55, 0.08)" strokeWidth="0.5" fill="none" />

    {/* Crown/head glow */}
    <ellipse cx="200" cy="60" rx="80" ry="60" fill="url(#crownGlow)" />
    
    {/* Heart center glow */}
    <ellipse cx="200" cy="180" rx="60" ry="50" fill="url(#heartGlow)" />

    {/* Three Pillars of the Tree of Life */}
    {/* Left Pillar - Severity (Boaz) */}
    <line x1="120" y1="80" x2="120" y2="420" stroke="url(#pillarLeft)" strokeWidth="2" opacity="0.6" />
    <circle cx="120" cy="80" r="8" fill="none" stroke="rgba(62, 76, 89, 0.5)" strokeWidth="1" />
    <circle cx="120" cy="180" r="8" fill="none" stroke="rgba(62, 76, 89, 0.5)" strokeWidth="1" />
    <circle cx="120" cy="280" r="8" fill="none" stroke="rgba(62, 76, 89, 0.5)" strokeWidth="1" />
    <circle cx="120" cy="380" r="8" fill="none" stroke="rgba(62, 76, 89, 0.5)" strokeWidth="1" />

    {/* Right Pillar - Mercy (Jachin) */}
    <line x1="280" y1="80" x2="280" y2="420" stroke="url(#pillarRight)" strokeWidth="2" opacity="0.6" />
    <circle cx="280" cy="80" r="8" fill="none" stroke="rgba(212, 175, 55, 0.4)" strokeWidth="1" />
    <circle cx="280" cy="180" r="8" fill="none" stroke="rgba(212, 175, 55, 0.4)" strokeWidth="1" />
    <circle cx="280" cy="280" r="8" fill="none" stroke="rgba(212, 175, 55, 0.4)" strokeWidth="1" />
    <circle cx="280" cy="380" r="8" fill="none" stroke="rgba(212, 175, 55, 0.4)" strokeWidth="1" />

    {/* Central Pillar - Equilibrium */}
    <line x1="200" y1="40" x2="200" y2="460" stroke="url(#centralPillar)" strokeWidth="2" />

    {/* Connecting paths between pillars */}
    <path d="M120 80 Q200 60 280 80" stroke="rgba(255,255,255,0.1)" strokeWidth="1" fill="none" />
    <path d="M120 180 L280 180" stroke="rgba(255,255,255,0.08)" strokeWidth="1" fill="none" strokeDasharray="4,4" />
    <path d="M120 280 Q200 260 280 280" stroke="rgba(255,255,255,0.1)" strokeWidth="1" fill="none" />
    <path d="M120 380 Q200 400 280 380" stroke="rgba(255,255,255,0.1)" strokeWidth="1" fill="none" />

    {/* Human figure silhouette - stylized */}
    {/* Head */}
    <ellipse cx="200" cy="55" rx="28" ry="32" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
    <circle cx="200" cy="50" r="22" fill="rgba(255,255,255,0.03)" stroke="rgba(212,175,55,0.3)" strokeWidth="1" />

    {/* Neck */}
    <rect x="190" y="85" width="20" height="20" rx="5" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />

    {/* Torso */}
    <path 
      d="M160 105 Q140 150 145 200 Q150 280 170 340 L230 340 Q250 280 255 200 Q260 150 240 105 Z" 
      fill="rgba(255,255,255,0.03)" 
      stroke="rgba(255,255,255,0.12)" 
      strokeWidth="1"
    />

    {/* Arms - stylized */}
    <path 
      d="M145 120 Q100 140 80 200 Q70 240 85 280" 
      fill="none" 
      stroke="rgba(255,255,255,0.1)" 
      strokeWidth="8"
      strokeLinecap="round"
    />
    <path 
      d="M255 120 Q300 140 320 200 Q330 240 315 280" 
      fill="none" 
      stroke="rgba(255,255,255,0.1)" 
      strokeWidth="8"
      strokeLinecap="round"
    />

    {/* Legs - stylized */}
    <path 
      d="M180 340 Q175 400 170 460" 
      fill="none" 
      stroke="rgba(255,255,255,0.1)" 
      strokeWidth="12"
      strokeLinecap="round"
    />
    <path 
      d="M220 340 Q225 400 230 460" 
      fill="none" 
      stroke="rgba(255,255,255,0.1)" 
      strokeWidth="12"
      strokeLinecap="round"
    />

    {/* Chakra/Sephiroth center points (glowing) */}
    {/* Crown - Kether */}
    <circle cx="200" cy="35" r="6" fill="rgba(212,175,55,0.6)" filter="url(#glow)" />
    
    {/* Third Eye - Da'at */}
    <circle cx="200" cy="65" r="4" fill="rgba(138,43,226,0.5)" filter="url(#glow)" />
    
    {/* Throat - Binah/Chokmah bridge */}
    <circle cx="200" cy="100" r="4" fill="rgba(100,149,237,0.5)" filter="url(#glow)" />
    
    {/* Heart - Tiphereth */}
    <circle cx="200" cy="165" r="7" fill="rgba(255,215,0,0.6)" filter="url(#glow)" />
    
    {/* Solar Plexus - Netzach/Hod bridge */}
    <circle cx="200" cy="215" r="4" fill="rgba(255,165,0,0.5)" filter="url(#glow)" />
    
    {/* Sacral - Yesod */}
    <circle cx="200" cy="280" r="5" fill="rgba(255,140,0,0.5)" filter="url(#glow)" />
    
    {/* Root - Malkuth */}
    <circle cx="200" cy="350" r="6" fill="rgba(139,69,19,0.6)" filter="url(#glow)" />

    {/* Aura lines */}
    <ellipse cx="200" cy="200" rx="90" ry="160" fill="none" stroke="rgba(212,175,55,0.08)" strokeWidth="1" strokeDasharray="8,8" />
    <ellipse cx="200" cy="200" rx="110" ry="190" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" strokeDasharray="12,12" />
  </svg>
);

export const AtlasFigure = ({ nodes, selectedSlug, onSelect, language }) => {
  return (
    <div className="glass-card orbital-glow grain-mask relative overflow-hidden rounded-[2rem] p-4 md:p-8" data-testid="atlas-figure-panel">
      <div className="relative mx-auto aspect-[4/5] w-full max-w-3xl overflow-hidden rounded-[1.75rem] border border-white/10 bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.14),transparent_22%),linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))]">
        {/* Rich SVG illustration */}
        <RitualBodySVG />

        {/* Caption badge */}
        <div className="absolute left-4 top-4 z-10 flex items-center gap-2 rounded-full border border-white/10 bg-black/50 px-3 py-2 text-[11px] uppercase tracking-[0.3em] text-primary md:left-8 md:top-8" data-testid="atlas-figure-caption">
          <Orbit className="h-3.5 w-3.5" />
          {pickText(language, "Temple Atlas", "Atlante del Tempio")}
        </div>

        {/* Interactive node buttons */}
        {nodes.map((node) => {
          const isSelected = selectedSlug === node.slug;
          return (
            <button
              key={node.slug}
              className={`absolute z-20 -translate-x-1/2 -translate-y-1/2 rounded-full border px-2 py-2 text-left transition-all duration-500 md:px-3 ${
                isSelected
                  ? "border-primary bg-primary text-background pulse-gold"
                  : "border-white/15 bg-black/55 text-white hover:border-primary/50 hover:bg-black/70"
              }`}
              data-testid={`atlas-node-${node.slug}`}
              onClick={() => onSelect(node.slug)}
              style={{ left: `${node.atlas_x}%`, top: `${node.atlas_y}%` }}
              type="button"
            >
              <span className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 shrink-0" />
                <span className="sr-only">{pickText(language, node.title_en, node.title_it)}</span>
              </span>
              {isSelected ? (
                <span
                  className={`pointer-events-none absolute hidden max-w-[12rem] md:block ${node.atlas_x >= 50 ? "left-full ml-3" : "right-full mr-3"} top-1/2 -translate-y-1/2`}
                  data-testid={`atlas-node-label-${node.slug}`}
                >
                  <span className="block rounded-2xl border border-primary/20 bg-black/85 px-4 py-3 shadow-2xl">
                    <span className="block text-sm font-semibold text-foreground">
                      {pickText(language, node.title_en, node.title_it)}
                    </span>
                    <span className="mt-1 block text-xs uppercase tracking-[0.24em] text-slate-400">
                      {pickText(language, node.preview_en, node.preview_it)}
                    </span>
                  </span>
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};